import { createSelector } from '@reduxjs/toolkit'
import { TRootState } from 'store/types'
import { IAiLibVideoListParams } from 'store/types/aiLib'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import { getStringifiedQuality } from 'store/features/aiVideo/utils'

const selector = (state: TRootState) => state.aiLibVideo

const activeTab = createSelector(selector, state => state.activeTab)
const isFiltersOpen = createSelector(selector, state => state.isFiltersOpen)
const filters = createSelector(selector, state => state.filters)
const isLoaded = createSelector(selector, state => state.isLoaded)
const isInitialLoading = createSelector(
  selector,
  state => state.isInitialLoading
)
const isLoadingMore = createSelector(selector, state => state.isLoadingMore)
const historyList = createSelector(selector, state => state.historyList)
const historyTotalCount = createSelector(
  selector,
  state => state.historyTotalCount
)

const selectedDuration = createSelector(filters, state => state.duration)
const selectedAspectRatio = createSelector(filters, state => state.aspectRatio)
const selectedQuality = createSelector(filters, state => state.quality)
const selectedModelCode = createSelector(filters, state => state.modelCode)
const isHistoryScopedToCurrentProject = createSelector(
  filters,
  state => state.isHistoryScopedToCurrentProject
)

const selectedModel = createSelector(
  [selectedModelCode, aiVideoSelectors.aiVideoModels],
  (modelCode, models) =>
    modelCode ? (models.find(m => m.model === modelCode) ?? null) : null
)

const availableDurationOptions = createSelector(
  [selectedModel, aiVideoSelectors.aiVideoModels],
  (model, allModels) => {
    if (model) {
      return [...model.options]
        .sort((a, b) => a.duration - b.duration)
        .map(option => ({
          value: option.duration,
          label: `${option.duration}s`
        }))
    }

    const allDurations = [
      ...new Set(allModels.flatMap(m => m.options.map(o => o.duration)))
    ].sort((a, b) => a - b)
    return allDurations.map(d => ({ value: d, label: `${d}s` }))
  }
)

const availableAspectRatioOptions = createSelector(
  [selectedModel, aiVideoSelectors.aiVideoModels],
  (model, allModels) => {
    if (model) {
      if (!model.capabilities.aspectRatios) return []
      return model.capabilities.aspectRatios.map(ratio => ({
        value: ratio,
        label: ratio
      }))
    }

    const allRatios = [
      ...new Set(allModels.flatMap(m => m.capabilities.aspectRatios || []))
    ]
    return allRatios.map(r => ({ value: r, label: r }))
  }
)

const collectQualities = (
  model: ReturnType<typeof selectedModel.resultFunc> | null,
  duration: string | number | null,
  allModels: ReturnType<typeof aiVideoSelectors.aiVideoModels.resultFunc>
): number[] => {
  if (!model) {
    return allModels.flatMap(m =>
      m.options.flatMap(o => o.qualities.map(q => q.quality))
    )
  }

  if (duration !== null) {
    const durationOption = model.options.find(o => o.duration === duration)
    return durationOption?.qualities.map(q => q.quality) ?? []
  }

  return model.options.flatMap(o => o.qualities.map(q => q.quality))
}

const availableQualityOptions = createSelector(
  [selectedModel, selectedDuration, aiVideoSelectors.aiVideoModels],
  (model, duration, allModels) => {
    const qualities = collectQualities(model, duration, allModels)

    // Dedup by label — different values can share a label (e.g. 768 and 720 → "720p HD")
    return [...new Set(qualities)]
      .sort((a, b) => a - b)
      .map(q => ({ value: q, label: getStringifiedQuality(q) }))
      .filter(
        (option, index, self) =>
          self.findIndex(o => o.label === option.label) === index
      )
  }
)

const hasActiveFilters = createSelector(filters, filtersState => {
  const {
    modelCode,
    duration,
    aspectRatio,
    quality,
    isHistoryScopedToCurrentProject: isScoped
  } = filtersState

  return !!(modelCode || duration || aspectRatio || quality || isScoped)
})

const activeFiltersCount = createSelector(filters, filtersState => {
  const {
    modelCode,
    duration,
    aspectRatio,
    quality,
    isHistoryScopedToCurrentProject: isScoped
  } = filtersState

  return [modelCode, duration, aspectRatio, quality, isScoped].filter(Boolean)
    .length
})

const activeFilters = createSelector(
  filters,
  (filters): Partial<IAiLibVideoListParams> => {
    const { modelCode, duration, aspectRatio, quality } = filters

    return {
      videoModel: modelCode ?? undefined,
      videoDurationSeconds: duration !== null ? Number(duration) : undefined,
      aspectRatio: aspectRatio !== null ? String(aspectRatio) : undefined,
      quality: quality !== null ? Number(quality) : undefined
    }
  }
)

export const aiLibSelectors = {
  activeTab,
  isFiltersOpen,
  filters,
  hasActiveFilters,
  activeFiltersCount,
  selectedDuration,
  selectedAspectRatio,
  selectedQuality,
  selectedModelCode,
  isHistoryScopedToCurrentProject,
  isLoaded,
  isInitialLoading,
  isLoadingMore,
  historyList,
  historyTotalCount,
  activeFilters,
  selectedModel,
  availableDurationOptions,
  availableAspectRatioOptions,
  availableQualityOptions
}
